/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-28 08:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Link, Button, Icon, Badge, Popover, ActionList } from '@shopify/polaris';
import { routes } from 'src/routes';
import { DuplicateMinor, MobileHorizontalDotsMajor } from '@shopify/polaris-icons';

import { RenderTooltip } from 'src/components/views/common/render-tooltip';

import {
  PERSONALIZED_RECOMMENDATION,
  COLUMN_WIDGET,
  RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES,
  RECOMMENDATION_WIDGET_STATUS
} from 'src/constants/recommendation.js';

import history from 'src/routes/history';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import { checkPersonalizationCampaignRemainingDays } from 'src/helpers';

type Props = {
  key: number,
  index: number,
  widget: Object,
  deleting: boolean,
  success: boolean,
  saving: boolean,
  hasAddonAIPowerRecommendation: boolean,
  enablePersonalizedRec: boolean,
  selectedTab: number,
  handleWidgetAction: ({ widgetId: string, widgetName: string }, type) => void,
  handleUnlock: (recType: string) => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  selectedId: string,
  isDisabledRow: boolean,
  activeModalRemove: boolean,
  activeActionList: boolean
};

class WidgetRow extends Component<ComposedProps, State> {
  state = {
    selectedId: '',
    activeModalRemove: false,
    isDisabledRow: false,
    activeActionList: false
  };

  componentDidMount() {
    const { widget, tenantApp } = this.props;
    const recommendationType = _.get(widget, 'recommendationType', '');
    const isDisabledRow = !_.get(
      tenantApp,
      `settings.recommendation.settings.${recommendationType}`,
      recommendationType === PERSONALIZED_RECOMMENDATION ? false : true
    );
    this.setState({ isDisabledRow });
  }

  componentWillReceiveProps(nextProps) {
    const {
      widget: widgetPrev,
      hasAddonAIPowerRecommendation: hasAddonAIPowerRecommendationPrev,
      enablePersonalizedRec: enablePersonalizedRecPrev
    } = this.props;
    const { hasAddonAIPowerRecommendation, widget, enablePersonalizedRec, tenantApp } = nextProps;
    const recommendationType = _.get(widget, 'recommendationType', '');

    if (
      widgetPrev !== widget ||
      hasAddonAIPowerRecommendationPrev !== hasAddonAIPowerRecommendation ||
      enablePersonalizedRec !== enablePersonalizedRecPrev
    ) {
      const isDisabledRow = !_.get(
        tenantApp,
        `settings.recommendation.settings.${recommendationType}`,
        recommendationType === PERSONALIZED_RECOMMENDATION ? false : true
      );
      this.setState({ isDisabledRow });
    }
  }
  render() {
    const { widget } = this.props;

    return (
      <tr>
        {_.map(COLUMN_WIDGET, (column, index) => this.renderContent(widget, column.value, index))}
      </tr>
    );
  }

  /* #region: Render content components */
  renderContent = (item, column, index) => {
    switch (column) {
      case 'name':
        return this.renderWidgetName(item, index);
      case 'status':
        return this.renderStatus(item, index);
      case 'widgetId':
        return this.renderWidgetId(item, index);
      case 'type':
        return this.renderRecommendationType(item, index);
      case 'actions':
        return this.renderActions(item, index);
      default:
        return '';
    }
  };

  // Recommendation widget id
  renderWidgetId = (item, index) => {
    const widgetId = _.get(item, 'id', '');
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--widgetId"
        key={index}>
        {widgetId}
        <span className="copy">
          <Button icon={DuplicateMinor} plain onClick={() => this.copyClipBoard(widgetId)} />
        </span>
      </td>
    );
  };

  // Recommendation widget name
  renderWidgetName = (item, index) => {
    const { isDisabledRow } = this.state;
    const { handleUnlock } = this.props;
    const widgetName = _.get(item, 'widgetName', '');
    const recommendationType = _.get(item, 'recommendationType', '');
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--name"
        key={index}>
        <Link
          onClick={() => {
            isDisabledRow
              ? handleUnlock(recommendationType)
              : history.push(routes.recommendationWidgets.view(item.id));
          }}
          removeUnderline>
          {widgetName}
        </Link>
      </td>
    );
  };

  // Recommendation widget recommendation type
  renderRecommendationType = (item, index) => {
    const recommendationType = _.get(item, 'recommendationType', '');
    const { i18n } = this.props;
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--type"
        key={index}>
        <p>
          {!_.isEmpty(recommendationType)
            ? i18n.translate(RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES[recommendationType]?.name)
            : ''}
        </p>
      </td>
    );
  };

  // Recommendation widget status
  renderStatus = (item, index) => {
    const { isDisabledRow } = this.state;
    const { i18n, widget, selectedTab } = this.props;
    const recommendationType = _.get(widget, 'recommendationType', '');

    /* #region Check personalization campaign */
    if (recommendationType === PERSONALIZED_RECOMMENDATION && selectedTab === 0) {
      const remainingDays = checkPersonalizationCampaignRemainingDays();
      if (remainingDays !== -1) {
        return (
          <td
            className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--status BC-MarketingCampaign_remainingDays"
            key={index}>
            <Badge status={remainingDays > 5 ? 'attention' : 'critical'}>
              {i18n.translate('AdminHTML.MarketingCampaign.Features.remainingDays', {
                count: remainingDays,
                nt_number: remainingDays
              })}
            </Badge>
          </td>
        );
      }
    }
    /* #endregion */

    let status = 'default';
    let label = i18n.translate(RECOMMENDATION_WIDGET_STATUS[selectedTab].labelDisplay);
    if (isDisabledRow) {
      let content = i18n.translate('AdminHTML.Recommendation.WidgetDashboard.statusLock_Tooltip');
      if (recommendationType === PERSONALIZED_RECOMMENDATION) {
        content = i18n.translate(
          'AdminHTML.Recommendation.WidgetDashboard.statusPackageLock_Tooltip'
        );
      }
      status = 'warning';
      label = (
        <RenderTooltip
          content={content}
          children={i18n.translate('AdminHTML.Common.Badge.locked')}
          icon={false}
        />
      );
    } else {
      // Update badge status in live tab
      if (selectedTab === 0) {
        status = 'success';
      }
    }
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--status"
        key={index}>
        <Badge status={status} progress="complete">
          {label}
        </Badge>
      </td>
    );
  };

  // Recommendation widget actions
  renderActions = (item, index) => {
    const { i18n, selectedTab, handleWidgetAction, handleUnlock } = this.props;
    const { isDisabledRow, activeActionList } = this.state;
    const widgetId = _.get(item, 'id', '');
    const widgetName = _.get(item, 'widgetName', '');
    const recommendationType = _.get(item, 'recommendationType', '');
    const activator = (
      <span className="group_btn">
        <Button onClick={this.togglePopoverAction} plain>
          <Icon source={MobileHorizontalDotsMajor} />
        </Button>
      </span>
    );

    let menuActions = [
      {
        content: i18n.translate('AdminHTML.Recommendation.WidgetDashboard.pauseAction_Title'),
        id: 'pause',
        onAction: () => this.handleWidgetAction({ widgetId, widgetName }, 'pause')
      },
      {
        content: i18n.translate('AdminHTML.Recommendation.WidgetDashboard.editAction_Title'),
        id: 'edit',
        onAction: () => this.handleEdit(widgetId)
      },
      {
        content: i18n.translate('AdminHTML.Recommendation.WidgetDashboard.duplicateAction_Title'),
        id: 'duplicate',
        onAction: () => this.handleWidgetAction({ widgetId, widgetName }, 'duplicate')
      },
      {
        content: i18n.translate('AdminHTML.Recommendation.WidgetDashboard.archiveAction_Title'),
        id: 'archive',
        onAction: () => this.handleWidgetAction({ widgetId, widgetName }, 'archive')
      },
      {
        content: i18n.translate('AdminHTML.Recommendation.WidgetDashboard.restoreAction_Title'),
        id: 'restore',
        onAction: () => this.handleWidgetAction({ widgetId, widgetName }, 'restore')
      },
      {
        content: i18n.translate('AdminHTML.Recommendation.WidgetDashboard.deleteAction_Title'),
        id: 'delete',
        onAction: () => this.handleWidgetAction({ widgetId, widgetName }, 'delete')
      },
      {
        content: i18n.translate('AdminHTML.Recommendation.WidgetDashboard.analytics_Title'),
        id: 'analytics',
        onAction: () =>
          history.push(routes.analytics.recommendationWidgetPerformance(), {
            highlightId: widgetId
          })
      }
    ];
    switch (selectedTab) {
      case 0: {
        _.remove(menuActions, action => ['archive', 'restore'].includes(action.id));
        if (isDisabledRow) {
          _.remove(menuActions, action => ['duplicate', 'pause'].includes(action.id));
        }
        break;
      }
      case 1: {
        _.remove(menuActions, action => ['pause', 'restore'].includes(action.id));
        if (isDisabledRow) {
          _.remove(menuActions, action => action.id === 'duplicate');
        }
        break;
      }
      case 2: {
        _.remove(menuActions, action => ['pause', 'duplicate', 'archive'].includes(action.id));
        break;
      }
      default:
    }

    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--actions"
        key={index}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {selectedTab === 1 && (
            <span className="btn-launch">
              <Button
                onClick={() =>
                  isDisabledRow
                    ? handleUnlock(recommendationType)
                    : handleWidgetAction({ widgetId, widgetName }, 'launch')
                }>
                {i18n.translate('AdminHTML.Recommendation.WidgetDashboard.launchAction_Title')}
              </Button>
            </span>
          )}
          <Popover
            active={activeActionList}
            activator={activator}
            onClose={this.togglePopoverAction}>
            <Popover.Pane>
              <ActionList items={menuActions} />
            </Popover.Pane>
          </Popover>
        </div>
      </td>
    );
  };

  copyClipBoard = (copyText: string) => {
    const { i18n } = this.props;
    try {
      const inputText = document.createElement('input');
      inputText.value = copyText;
      document.body.appendChild(inputText);
      inputText.select();

      document.execCommand('copy');
      document.body.removeChild(inputText);
      showSABToast(i18n.translate('AdminHTML.Common.Action.copied', { t: copyText }), false);
    } catch (error) {}
  };

  togglePopoverAction = () => {
    this.setState(({ activeActionList }) => {
      return { activeActionList: !activeActionList };
    });
  };

  handleEdit = widgetId => {
    const { isDisabledRow } = this.state;
    const { widget, handleUnlock } = this.props;
    if (isDisabledRow) {
      const recommendationType = _.get(widget, 'recommendationType', '');
      handleUnlock(recommendationType);
      this.togglePopoverAction();
    } else {
      history.push(routes.recommendationWidgets.view(widgetId));
    }
  };

  // Handle action on popover
  handleWidgetAction = (widget, actionType) => {
    const { handleWidgetAction } = this.props;
    handleWidgetAction(widget, actionType);
    this.togglePopoverAction();
  };
}

export default withI18n()(WidgetRow);
